import React, { memo, useCallback } from "react";
import { Clock, Timer, Trash2, Type, Copy } from 'lucide-react';
import "./HighlightEditor.css";

const HighlightEditor = memo(({ highlight, onUpdate, onDelete, onDuplicate, onGenerateHighlight }) => {
  const adjustValue = useCallback((field, increment) => {
    const newValue = highlight[field] + increment;
    if (newValue >= 0) {
      onUpdate(field, newValue);
    }
  }, [highlight, onUpdate]);

  const handleTextChange = useCallback((e) => {
    onUpdate('text', e.target.value);
  }, [onUpdate]);

  const handleDurationChange = useCallback((e) => {
    const duration = parseFloat(e.target.value);
    onUpdate('duration', duration);
  }, [onUpdate]);

  const toggleSlowMotion = useCallback(() => {
    onUpdate('slowMotion', !highlight.slowMotion);
  }, [highlight.slowMotion, onUpdate]);

  const toggleZoom = useCallback(() => {
    // If zoom is being enabled for the first time, set default values
    if (!highlight.zoom) {
      onUpdate('zoom', {
        enabled: true,
        x: 0,
        y: 0
      });
    } else {
      // Toggle the enabled state
      onUpdate('zoom', {
        ...highlight.zoom,
        enabled: !highlight.zoom.enabled
      });
    }
  }, [highlight.zoom, onUpdate]);

  const handleColorChange = useCallback((e) => {
    onUpdate('color', e.target.value);
  }, [onUpdate]);

  const handleDelete = useCallback(() => {
    onDelete(highlight.id);
  }, [highlight.id, onDelete]);

  const formatTime = useCallback((timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    const hundredths = Math.floor((timeInSeconds % 1) * 100);
    return `${minutes}:${seconds.toString().padStart(2, '0')}:${hundredths.toString().padStart(2, '0')}`;
  }, []);

  return (
    <div className="highlight-editor">
      <div className="editor-header">
        <h2 className="editor-title">Edit Highlight</h2>
        <div className="editor-header-actions">
          <button onClick={onDuplicate} className="action-btn duplicate-btn" title="Duplicate highlight">
            <Copy size={20} />
          </button>
          <button onClick={handleDelete} className="action-btn delete-btn" title="Delete highlight">
            <Trash2 size={20} />
          </button>
        </div>
      </div>

      <div className="editor-controls">
        {/* Time Controls and Toggle Row */}
        <div className="control-section">
          <div className="time-toggles-container">
            {/* Start Time */}
            <div className="time-section">
              <div className="start-time-row">
                <Clock size={16} />
                <span className="control-label">Start Time</span>
              </div>
              <div className="adjust-controls">
                <button onClick={() => adjustValue("startTime", -0.1)} className="minus-btn">-</button>
                <input
                  type="text"
                  value={formatTime(highlight.startTime)}
                  readOnly
                  className="time-display-input"
                />
                <button onClick={() => adjustValue("startTime", 0.1)} className="plus-btn">+</button>
              </div>
            </div>

            {/* Toggles */}
            <div className="toggles-container">
              <div className="toggle-item">
                <span className="toggle-label">Slow-Mo</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={highlight.slowMotion || false}
                    onChange={toggleSlowMotion}
                  />
                  <span className="toggle-slider round"></span>
                </label>
              </div>

              <div className="toggle-item">
                <span className="toggle-label">Zoom</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={highlight.zoom?.enabled || false}
                    onChange={toggleZoom}
                  />
                  <span className="toggle-slider round"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="control-group duration-group">
            <div className="duration-row">
              <Timer size={16} />
              <span className="control-label">Duration: {highlight.duration.toFixed(1)}s</span>
            </div>
            <div className="slider-control">
              <input
                type="range"
                min="1"
                max="15"
                step="0.1"
                value={highlight.duration}
                onChange={handleDurationChange}
                className="duration-slider"
              />
            </div>
          </div>
        </div>

        {/* Text Overlay Controls */}
        <div className="control-section">
          <div className="text-control-group">
            <div className="text-input-wrapper">
              <div className="text-overlay-row">
                <Type size={16} />
                <span className="control-label">Text Overlay</span>
              </div>
              <input
                type="text"
                value={highlight.text || ''}
                onChange={handleTextChange}
                placeholder="Add text overlay..."
                className="text-input"
              />
            </div>

            <div className="color-wrapper">
              <div className="color-label">Color</div>
              <div className="color-picker-wrapper">
                <input
                  type="color"
                  value={highlight.color || '#ffffff'}
                  onChange={handleColorChange}
                  className="color-picker"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Generate Highlights Button */}
        <button
          className="generate-highlights-btn"
          onClick={onGenerateHighlight}
        >
          Generate Highlights video
        </button>
      </div>
    </div>
  );
});

HighlightEditor.displayName = 'HighlightEditor';

export default HighlightEditor;
